import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useModalityById } from '@/hooks/use-modality-by-id'
import { useUpdateModality } from '@/hooks/use-update-modality'
import { ChevronLeft, Save } from 'lucide-react'
import { handleConfirmNavigation } from '../ui/confirm-navigation-popup'
import { ConfirmModal } from '../ui/confirmation'
import { PageContainer } from '@/component/layout'
//import type { ModalityPatch } from '@/hooks/use-update-modality'

export const EditModality = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { item, loading, error } = useModalityById(id)
  const { updateModality, submitting, error: saveError, setError } = useUpdateModality()

  const [form, setForm] = useState({
    name: '',
    description: '',
    keyFeatures: '',
    status: true,
    isManualEntry: false,
    changeDescription: '',
  })

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name ?? '',
        description: item.description ?? '',
        keyFeatures: item.keyFeatures ?? '',
        status: !!item.status,
        isManualEntry: !!item.isManualEntry,
        changeDescription: item.changeDescription ?? '',
      })
    }
  }, [item])

  const onChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.currentTarget.type === 'checkbox' ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value
    setForm((prev) => ({ ...prev, [key]: value as any }))
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    if (!form.name.trim() || !form.description.trim()) {
      if (!form.name.trim() && !form.description.trim()) {
        setError('Name and Description are required.')
      } else if (!form.name.trim()) {
        setError('Name is required.')
      } else if (!form.description.trim()) {
        setError('Description is required.')
      }
      return
    }

    await updateModality(id, {
      name: form.name.trim(),
      description: form.description.trim(),
      keyFeatures: form.keyFeatures || null,
      status: form.status,
      isManualEntry: form.isManualEntry,
      changeDescription: form.changeDescription || null,
    })

    navigate(`/modalities/${id}`, { state: { updated: true } })
  }

  if (!id) return <div className='p-6'>Missing id</div>
  if (loading) return <div className='p-6 text-center'>Loading…</div>
  if (error) return <div className='p-6 text-red-600'>Error: {error}</div>
  if (!item) return <div className='p-6'>Not found</div>

  return (
    <PageContainer className='w-[60vw]'>
      {/* Back link */}
      <div className='mb-6'>
          <button
            onClick={() => handleConfirmNavigation(navigate, '/modalities')}
            className='inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900'
          >
            <span className='-ml-1'>
              <ChevronLeft />
            </span>
            Back to Modality Manager
          </button>
      </div>

      <form onSubmit={onSubmit} className='space-y-6'>
        {/* Header */}
        <div className='mb-4 flex items-center justify-between'>
          <h1 className='text-3xl font-bold'>Edit modality</h1>
          <button type='submit' disabled={submitting} className='rounded-lg bg-black text-white px-4 py-2 hover:bg-gray-800 disabled:opacity-60'>
            {submitting ? 'Saving…' : 'Save'}
            <Save className='inline-block ml-2' size={18} />
          </button>
        </div>

        {saveError ? <div className='mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700'>{saveError}</div> : null}

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Modality Name *</label>
          <input
            type='text'
            value={form.name}
            onChange={onChange('name')}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-blue-300'
            placeholder='e.g., Problem-Based Learning (PBL)'
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>Description *</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={onChange('description')}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-blue-300'
            placeholder='Describe the learning modality…'
          />
        </div>
      </form>
      <ConfirmModal />
    </PageContainer>
  )
}
